import logger from '@/lib/logger';

export async function blobToBase64(blob: Blob): Promise<string> {
  if (typeof window !== 'undefined') {
    // Browser environment - use FileReader
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // Remove the data URL prefix
      };
      reader.onerror = () => reject(new Error('Failed to read Blob as Base64'));
      reader.readAsDataURL(blob);
    });
  } else {
    // Server environment - use Buffer
    const buffer = Buffer.from(await blob.arrayBuffer());
    return buffer.toString('base64');
  }
}

export function base64ToBlob(base64: string, contentType = 'image/jpeg'): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: BlobPart[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays as BlobPart[], { type: contentType });
}

export function arrayBufferToBlob(arrayBuffer, mimeType) {
  // Convert the ArrayBuffer to a Blob using the mimeType
  return new Blob([arrayBuffer], { type: mimeType });
}

export function readImageFromURL(url: string): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onload = function (event) {
          resolve(event?.target?.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob); // Convert blob to Base64
      })
      .catch((error) => {
        logger.error('Error reading image:', error);
        reject(undefined);
      });
  });
}

export function compressImageBlob(
  blob: Blob,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url); // Free up memory

      let { width, height } = img;

      // Scale down while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context is not supported'));
        return;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image onto the canvas
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to Blob with compression
      canvas.toBlob(
        (compressedBlob) => {
          if (compressedBlob) {
            resolve(compressedBlob);
          } else {
            reject(new Error('Image compression failed'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

export async function imageUrlToBlob(url: string): Promise<Blob | null> {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    logger.error('Failed to convert image URL to Blob:', error);
    return null;
  }
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string = 'image/png',
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob() failed'));
        }
      },
      type,
      quality
    );
  });
}

export const checkImageOnLoad = async (
  path: string,
  options?: {
    crossOrigin?: 'anonymous';
  }
): Promise<HTMLImageElement | undefined> => {
  const img = new Image();
  img.src = path;
  if (options && options.crossOrigin) {
    img.crossOrigin = options.crossOrigin;
  }

  return new Promise((resolve) => {
    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      logger.error(`Error checkImageOnLoad path: ${path}`);
      resolve(undefined);
    };
  });
};

/**
 * Loads images in batches and logs errors with detailed information.
 * @param {string[]} imageUrls - Array of image URLs to load.
 * @param {number} batchSize - Number of images to load concurrently.
 * @param {object} options - Callback after each image loads.
 * @param {(img: HTMLImageElement, index: number) => void} onImageLoad - Callback after each image loads.
 * @returns {Promise<void>} Resolves when all images are attempted to load.
 */
export async function loadImagesInBatches(
  imageUrls: string[],
  batchSize: number = 10,
  onImageLoad?: (img: HTMLImageElement | undefined, index: number) => void,
  options?: {
    crossOrigin?: 'anonymous';
  }
): Promise<void> {
  const validBatchSize = Math.max(batchSize, 1); // prevent input less than 1
  for (let i = 0; i < imageUrls.length; i += validBatchSize) {
    const batchPromises = imageUrls
      .slice(i, i + validBatchSize)
      .map((url, index) =>
        checkImageOnLoad(url, options).then((img) => {
          if (onImageLoad !== undefined) {
            onImageLoad(img, i + index);
            return img;
          }
        })
      );
    await Promise.allSettled(batchPromises);
  }
}

export const DEFAULT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Src that should bypass image CDN processing (ImageKit, Cloudflare Images, Contentstack, etc.) */
export const isUnprocessableImageUrl = (src: string): boolean =>
  !src ||
  src.startsWith('data:') ||
  src.startsWith('/') ||
  src.endsWith('.svg');

export const isSvg = (src: string = ''): boolean => {
  return src?.startsWith('data:image/svg') || src?.endsWith('.svg');
};

export const isPng = (src: string = ''): boolean => /\.png(\?|#|$)/i.test(src);
